import { useState, useRef, useEffect } from 'react'
import type { ScheduleData } from '../api'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { CalendarClock } from 'lucide-react'
import SchedulerDialog from './SchedulerDialog'

export interface ProfilerProjectModalResult {
  name: string
  comment: string
  color: string
  schedule: ScheduleData
}

const PROJECT_COLORS = [
  { value: '', label: 'По умолчанию', hex: '#3B82F6' },
  { value: '#3b82f6', label: 'Синий', hex: '#3b82f6' },
  { value: '#22c55e', label: 'Зелёный', hex: '#22c55e' },
  { value: '#8b5cf6', label: 'Фиолетовый', hex: '#8b5cf6' },
  { value: '#ef4444', label: 'Красный', hex: '#ef4444' },
  { value: '#eab308', label: 'Жёлтый', hex: '#eab308' },
]

interface ProfilerProjectModalProps {
  open: boolean
  mode: 'create' | 'edit'
  onClose: () => void
  onSubmit: (data: ProfilerProjectModalResult) => void
  onArchive?: (archived: boolean) => void
  initial?: {
    name?: string
    comment?: string
    color?: string
    archived?: boolean
    schedule?: Partial<ScheduleData>
  }
}

const SCHED_TYPE_LABELS: Record<string, string> = {
  time: 'Запуск по времени',
  interval: 'С интервалом',
  hourly: 'Каждый час',
  daily: 'Ежедневно',
  continuous: 'Непрерывно',
}

export default function ProfilerProjectModal({ open, mode, onClose, onSubmit, onArchive, initial }: ProfilerProjectModalProps) {
  const [name, setName] = useState('')
  const [comment, setComment] = useState('')
  const [color, setColor] = useState('')
  const [schedEnabled, setSchedEnabled] = useState(false)
  const [schedData, setSchedData] = useState<ScheduleData>({ enabled: false })
  const [schedDialogOpen, setSchedDialogOpen] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const prevOpenRef = useRef(false)

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setName(initial?.name ?? '')
      setComment(initial?.comment ?? '')
      setColor(initial?.color ?? '')
      const sc = initial?.schedule
      setSchedEnabled(sc?.enabled ?? false)
      setSchedData({ enabled: sc?.enabled ?? false, ...sc })
      setSchedDialogOpen(false)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
    prevOpenRef.current = open
  }, [open, initial])

  const handleSubmit = () => {
    const trimmed = name.trim()
    if (!trimmed) return
    onSubmit({
      name: trimmed,
      comment: comment.trim(),
      color,
      schedule: { ...schedData, enabled: schedEnabled },
    })
    onClose()
  }

  const schedSummary = (() => {
    if (!schedEnabled) return ''
    const t = schedData.type || 'time'
    const parts: string[] = [SCHED_TYPE_LABELS[t] || t]
    if ((t === 'time' || t === 'daily') && schedData.start_time) parts.push(`в ${schedData.start_time}`)
    if (t === 'hourly') parts.push(`в :${String(schedData.hourly_minute ?? 0).padStart(2, '0')}`)
    if (t === 'interval') {
      if (schedData.run_hours || schedData.run_minutes) parts.push(`${schedData.run_hours ?? 0}ч ${schedData.run_minutes ?? 0}м`)
      parts.push(`пауза ${schedData.interval_minutes ?? 10} мин`)
    }
    if (schedData.days && schedData.days !== 'Каждый день') parts.push(schedData.days)
    return parts.join(' · ')
  })()

  const isEdit = mode === 'edit'
  const isArchived = initial?.archived ?? false

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col overflow-hidden p-0 min-w-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>{isEdit ? 'Настройки проекта профайлера' : 'Новый проект профайлера'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Имя, описание и планировщик прогрева' : 'Каждый проект — отдельная конфигурация прогрева профилей'}
          </DialogDescription>
        </DialogHeader>

        {/* min-h-0 is critical for flex+scroll on Firefox: without it the
            scrollable child refuses to shrink below its content height,
            so when content > viewport part of the form is clipped off
            with no scrollbar. */}
        <div className="px-6 pb-5 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div className="space-y-2">
            <Label className="text-xs">Название</Label>
            <Input
              ref={inputRef}
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); if (e.key === 'Escape') onClose() }}
              placeholder="Например: Глубокий прогрев — Москва"
              className="h-9 text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Комментарий</Label>
            <Textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Описание проекта прогрева..."
              rows={2}
              className="text-sm resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Цвет</Label>
            <div className="flex items-center gap-2">
              {PROJECT_COLORS.map(c => {
                const isDefault = c.value === ''
                const isActive = color === c.value
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setColor(c.value)}
                    className="w-7 h-7 rounded-full transition-all duration-150 shrink-0 relative"
                    title={c.label}
                    style={isDefault ? {
                      background: 'transparent',
                      border: isActive ? '2px dashed var(--foreground)' : '2px dashed color-mix(in oklab, var(--muted-foreground) 60%, transparent)',
                      boxShadow: isActive ? '0 0 0 2px color-mix(in oklab, var(--foreground) 30%, transparent)' : 'none',
                      opacity: isActive ? 1 : 0.7,
                    } : {
                      background: c.hex,
                      border: isActive ? '2px solid var(--foreground)' : '2px solid transparent',
                      boxShadow: isActive ? `0 0 0 2px ${c.hex}` : 'none',
                      opacity: isActive ? 1 : 0.6,
                    }}
                  >
                    {isDefault && (
                      <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[9px] font-medium text-muted-foreground/80">
                        авто
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium">Автозапуск по расписанию</Label>
            <Switch checked={schedEnabled} onCheckedChange={setSchedEnabled} />
          </div>

          {!schedEnabled && schedSummary && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
              ⚠ Расписание настроено ({schedSummary}), но автозапуск <b>выключен</b>.
              Включите переключатель выше, чтобы расписание начало работать.
            </div>
          )}

          {schedEnabled && (
            <div className="space-y-2 pl-3 border-l-2 border-border">
              {schedSummary && (
                <p className="text-xs text-muted-foreground">{schedSummary}</p>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2 text-xs"
                onClick={() => setSchedDialogOpen(true)}
              >
                <CalendarClock className="w-3.5 h-3.5" />
                Настроить расписание
              </Button>
            </div>
          )}

          {schedEnabled && (
            <SchedulerDialog
              open={schedDialogOpen}
              onClose={() => setSchedDialogOpen(false)}
              initial={schedData}
              onSave={async (data) => { setSchedData(data) }}
              parentEnabled={schedEnabled}
            />
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-border bg-muted/50 sm:justify-between">
          <div>
            {isEdit && onArchive && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { onArchive(!isArchived); onClose() }}
                className="text-xs"
              >
                {isArchived ? 'Из архива' : 'В архив'}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>Отмена</Button>
            <Button size="sm" onClick={handleSubmit} disabled={!name.trim()}>
              {isEdit ? 'Сохранить' : 'Создать'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
